import { useCallback, useMemo, useState } from 'react';
import { Flex, Badge } from 'antd';
import { IconFileTypeCsv } from '@tabler/icons-react';
import { ConfigName, CONFIG_NAMES } from './ComponentSchemaManager.types';
import { TabContentContainer } from '~/app/components/knowledgeBase/TabContent';
import { ComponentSchemaManager } from './ComponentSchemaManager';
import { useCsvUpload } from '~/app/hooks/knowledgeBase/useCsvUpload';

export const ComponentSchemaManagerContainer = () => {
  const [atmmCreateOpen, setAtmmCreateOpen] = useState(false);
  const {
    fileConfigs,
    activeTab,
    viewModes,
    uploads,
    lookups,
    submitting,
    editingRows,
    associations,
    transferModal,
    onTabChange,
    onToggleView,
    onFileUpload,
    onFileRemove,
    onSubmit,
    onDownloadTemplate,
    onAddRow,
    onEditRow,
    onDeleteRow,
    onSaveRow,
    onCancelEdit,
    onApplySynonyms,
    onRefresh,
    onMatchySubmit,
    onOpenTransfer,
    onCloseTransfer,
    onSaveTransfer,
    pagination,
    onPageChange,
    searchTerms,
    onSearch,
  } = useCsvUpload();

  const handleAddRow = useCallback(
    (configName: string) => {
      if (configName === ConfigName.ASSET_TYPE_MANUFACTURER_MODEL) {
        setAtmmCreateOpen(true);
        return;
      }
      onAddRow(configName);
    },
    [onAddRow],
  );

  const handleAtmmCreateSuccess = useCallback(() => {
    onRefresh(ConfigName.ASSET_TYPE_MANUFACTURER_MODEL);
  }, [onRefresh]);

  const associationCountsMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    // to translate I18n
    for (const configName of CONFIG_NAMES) {
      const assocMap = associations[configName] || {};
      const counts: Record<string, number> = {};
      for (const [code, targets] of Object.entries(assocMap)) {
        counts[code] = targets.length;
      }
      map[configName] = counts;
    }

    // For the Asset-Subsystem-ComponentClass-ComponentType Mapping tab, use the
    // associatedComponentTypesCount field from the lookup row directly.
    // This avoids needing to open each modal to see the count.
    const componentTypeComponentClassRows =
      lookups[ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING]
        ?.rows || [];
    const componentTypeCtCounts: Record<string, number> = {};
    for (const row of componentTypeComponentClassRows) {
      const code = row.code as string;
      const count = row.associatedComponentTypesCount as number;
      if (code) componentTypeCtCounts[code] = count ?? 0;
    }
    // Override the association-map based count with the server-side count
    map[ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING] =
      componentTypeCtCounts;

    // Sync Component Classes count for Asset-Subsystem-ComponentClass Mapping
    const ccRows =
      lookups[ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING]?.rows || [];
    const ccCounts: Record<string, number> = {};
    for (const row of ccRows) {
      const code = row.code as string;
      const count = row.associatedComponentClassCount as number;
      if (code) ccCounts[code] = count ?? 0;
    }
    map[ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING] = ccCounts;

    return map;
  }, [associations, lookups]);

  const transferTargetItems = useMemo(() => {
    if (!transferModal.open) return [];
    const counterpart = transferModal.targetConfigName
      ? transferModal.targetConfigName
      : transferModal.sourceConfigName === ConfigName.ASSET_TYPE
        ? ConfigName.SUBSYSTEM
        : ConfigName.ASSET_TYPE;
    return lookups[counterpart]?.rows || [];
  }, [transferModal, lookups]);

  const transferSelectedKeys = useMemo(() => {
    if (!transferModal.open) return [];
    const selectedKeys =
      associations[transferModal.sourceConfigName]?.[
        transferModal.sourceCode
      ] || [];
    return selectedKeys;
  }, [transferModal, associations]);

  const tabItems = useMemo(
    () =>
      fileConfigs.map((config) => {
        const lookup = lookups[config.name];
        const totalItems = pagination?.[config.name]?.total;
        const hasData =
          (typeof totalItems === 'number' && totalItems > 0) ||
          (!!lookup && lookup.rows.length > 0);

        return {
          key: config.name,
          label: (
            <Flex align="center" gap={8}>
              <IconFileTypeCsv size={16} />
              <span>{config.name}</span>
              <Badge
                status={hasData ? 'success' : 'default'}
                text={
                  typeof totalItems === 'number'
                    ? `${totalItems}`
                    : hasData
                      ? `${lookup.rows.length}`
                      : ''
                }
                style={{ fontSize: 11 }}
              />
            </Flex>
          ),
          children: (
            <TabContentContainer
              config={config}
              viewMode={viewModes[config.name] || 'crud'}
              onToggleView={(viewMode) => onToggleView(config.name, viewMode)}
              uploadState={
                uploads[config.name] || {
                  file: null,
                  status: 'idle',
                  data: [],
                  errors: [],
                  fileName: config.fileName,
                }
              }
              onFileUpload={(file) => onFileUpload(config.name, file)}
              onFileRemove={() => onFileRemove(config.name)}
              onSubmit={() => onSubmit(config.name)}
              onDownloadTemplate={() => onDownloadTemplate(config.name)}
              isSubmitting={submitting[config.name] || false}
              lookupData={lookup || { rows: [], loading: false, error: null }}
              editingRows={editingRows[config.name] || {}}
              onAddRow={() => handleAddRow(config.name)}
              onEditRow={(key, row) => onEditRow(config.name, key, row)}
              onDeleteRow={(key) => onDeleteRow(config.name, key)}
              onSaveRow={(key) => onSaveRow(config.name, key)}
              onCancelEdit={(key) => onCancelEdit(config.name, key)}
              onApplySynonyms={
                onApplySynonyms
                  ? (key, synonyms) =>
                      onApplySynonyms(config.name, key, synonyms)
                  : undefined
              }
              onRefresh={() => onRefresh(config.name)}
              search={searchTerms?.[config.name]}
              onSearch={(q) => onSearch && onSearch(config.name, q)}
              onMatchySubmit={(data) => onMatchySubmit(config.name, data)}
              pagination={pagination ? pagination[config.name] : undefined}
              onPageChange={(page, pageSize) =>
                onPageChange && onPageChange(config.name, page, pageSize)
              }
              associationCounts={
                config.name === ConfigName.ASSET_TYPE ||
                config.name ===
                  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING ||
                config.name ===
                  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING
                  ? associationCountsMap[config.name]
                  : undefined
              }
              onOpenTransfer={
                config.name === ConfigName.ASSET_TYPE ||
                config.name ===
                  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING ||
                config.name ===
                  ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING
                  ? (id, code, name, targetConfigName) => {
                      const target =
                        targetConfigName ||
                        (config.name ===
                        ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_MAPPING
                          ? ConfigName.COMPONENT_CLASS
                          : config.name ===
                              ConfigName.ASSET_SUBSYSTEM_COMPONENT_CLASS_COMPONENT_TYPE_MAPPING
                            ? ConfigName.COMPONENT_TYPE
                            : undefined);
                      onOpenTransfer(config.name, id, code, name, target);
                    }
                  : undefined
              }
            />
          ),
        };
      }),
    [
      fileConfigs,
      lookups,
      viewModes,
      uploads,
      submitting,
      editingRows,
      searchTerms,
      pagination,
      associationCountsMap,
      onToggleView,
      onFileUpload,
      onFileRemove,
      onSubmit,
      onDownloadTemplate,
      handleAddRow,
      onEditRow,
      onDeleteRow,
      onSaveRow,
      onCancelEdit,
      onApplySynonyms,
      onRefresh,
      onSearch,
      onMatchySubmit,
      onPageChange,
      onOpenTransfer,
    ],
  );

  return (
    <ComponentSchemaManager
      tabItems={tabItems}
      activeTab={activeTab}
      onTabChange={onTabChange}
      transferModal={transferModal}
      transferTargetItems={transferTargetItems}
      transferSelectedKeys={transferSelectedKeys}
      onSaveTransfer={onSaveTransfer}
      onCloseTransfer={onCloseTransfer}
      atmmCreateOpen={atmmCreateOpen}
      onCloseAtmmCreate={() => setAtmmCreateOpen(false)}
      onAtmmCreateSuccess={handleAtmmCreateSuccess}
    />
  );
};
